import { useQuery } from "@tanstack/react-query"
import { Download, ExternalLink, FileText, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  getDownloadUrl,
  type DownloadUrlRequest,
} from "@/features/documents/api/documents.api"

export interface PreviewDoc {
  name: string
  // Stored object key — its embedded extension is the most reliable type
  // hint, since some writers name keys `{filename}.ext-{timestamp}` (the
  // extension isn't at the end of the string).
  key: string
  request: DownloadUrlRequest
  uploadedAt?: string | null
}

interface DocumentPreviewDialogProps {
  doc: PreviewDoc | null
  onClose: () => void
}

type Kind = "image" | "pdf" | "other"

// Resolve the viewer kind from the filename extension. Delivery/contract
// document entries don't persist a MIME type, so we sniff the name and
// stored key. The extension isn't always at the end of the string —
// some writers name keys `{filename}.pdf-{timestamp}` — so we match the
// extension token anywhere (bounded by `.` and a non-word char / end).
function detectKind(...sources: string[]): Kind {
  const hay = sources.join(" ").toLowerCase()
  if (/\.(png|jpe?g|webp|gif|bmp|avif)(\W|$)/.test(hay)) return "image"
  if (/\.pdf(\W|$)/.test(hay)) return "pdf"
  return "other"
}

// Inline previewer shared across document lists (contract / order /
// delivery). Mints a fresh presigned URL each open so we never expose
// stale signed links, then renders it via iframe (PDFs) or img (images);
// unknown types degrade to an open-in-new-tab / download link.
export function DocumentPreviewDialog({
  doc,
  onClose,
}: DocumentPreviewDialogProps) {
  return (
    <Dialog open={!!doc} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="flex h-[95vh] w-[95vw] flex-col gap-3 p-0 sm:max-w-none">
        {doc ? (
          <PreviewBody doc={doc} />
        ) : (
          <DialogHeader className="border-b px-5 py-3">
            <DialogTitle />
          </DialogHeader>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PreviewBody({ doc }: { doc: PreviewDoc }) {
  const { t } = useTranslation("documents")

  const { data: url, isLoading } = useQuery({
    queryKey: ["documents", "preview", doc.request],
    queryFn: () => getDownloadUrl(doc.request).then((r) => r.url),
    staleTime: 30 * 60 * 1000, // signed URLs are valid for 1h
    gcTime: 30 * 60 * 1000,
  })

  const kind = detectKind(doc.key, doc.name)

  return (
    <>
      <DialogHeader className="border-b px-5 py-3">
        <DialogTitle className="truncate text-sm">{doc.name}</DialogTitle>
        <DialogDescription className="text-xs">
          {doc.uploadedAt ? doc.uploadedAt.slice(0, 10) : "—"}
        </DialogDescription>
      </DialogHeader>

      <div className="bg-muted/30 min-h-0 flex-1 overflow-hidden">
        {isLoading || !url ? (
          <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {t("preview.loading")}
          </div>
        ) : kind === "image" ? (
          <div className="flex h-full items-center justify-center p-4">
            <img
              src={url}
              alt={doc.name}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : kind === "pdf" ? (
          <iframe src={url} title={doc.name} className="h-full w-full border-0" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <FileText className="text-muted-foreground size-10" />
            <p className="text-sm">{t("preview.unsupported")}</p>
            <Button asChild variant="outline" size="sm">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-4" />
                {t("preview.openExternal")}
              </a>
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
        {url ? (
          <>
            <Button asChild variant="outline" size="sm">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-4" />
                {t("preview.openExternal")}
              </a>
            </Button>
            <Button asChild size="sm">
              <a href={url} download={doc.name}>
                <Download className="size-4" />
                {t("preview.download")}
              </a>
            </Button>
          </>
        ) : null}
      </div>
    </>
  )
}
